'use client';

import { Slot } from '@radix-ui/react-slot';
import {
  type ElementType,
  type MouseEvent,
  type MouseEventHandler,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { css, cx } from '@/styled-system/css';

import { useSlotContent } from '../../hooks/use-slot-content';
import { ProgressStepperRootContext } from './context/progress-stepper-root.context';
import { ProgressStepperStepContext } from './context/progress-stepper-step.context';
import { ProgressStepperDot } from './progress-stepper-dot';
import { ProgressStepperStepLabel } from './progress-stepper-step-label';
import { progressStepperStepRecipe } from './styles/progress-stepper-step.styles';
import type { ProgressStepperStepPropsInterface } from './types/progress-stepper-step.types';

/**
 * ProgressStepper's individual step.
 * Contains `ProgressStepper.Dot`, `ProgressStepper.StepLabel`, `ProgressStepper.LabelText`, and `ProgressStepper.Text`
 *
 * @since 8.42.0
 */
export const ProgressStepperStep = forwardRef<HTMLLIElement, ProgressStepperStepPropsInterface>(
  (props, forwardedRef) => {
    const {
      asChild = false,
      active = false,
      children,
      tone,
      interactive = false,
      label = '',
      className,
      href = '#',
      index,
      css: cssProp,
      renderLabel,
      meta,
      onClick,
      ...rest
    } = props;

    const { activeStepIndex, alignLabels, onStepClick, orientation, setCurrentLabel } = useContext(
      ProgressStepperRootContext,
    );

    const isActive = index === activeStepIndex || active;

    // Combining visual variants into a single property to avoid excessive compound variants
    const variant = isActive ? 'active' : tone || 'notStarted';

    const styles = css(
      progressStepperStepRecipe.raw({
        active,
        alignLabel: alignLabels,
        interactive,
        variant,
        orientation,
      }),
      cssProp,
    );

    const Component: ElementType = useMemo(() => {
      if (asChild) return Slot;
      return interactive ? 'a' : 'div';
    }, [interactive, asChild]);

    const content = useSlotContent({
      children,
      asChild,
      template: () => (
        <>
          <ProgressStepperDot />
          {renderLabel ? (
            renderLabel(props)
          ) : (
            <ProgressStepperStepLabel label={label} meta={meta} />
          )}
        </>
      ),
      dependencies: [renderLabel, label, meta, props],
    });

    const handleClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
      (event) => {
        onStepClick?.(event, index!);
        onClick?.(event as unknown as MouseEvent<HTMLLIElement>);
        if (href === '#') event.preventDefault();
      },
      [index, onClick, onStepClick, href],
    );

    useEffect(() => {
      if (isActive) {
        setCurrentLabel(label, meta ?? null);
      }
    }, [isActive, label, meta, setCurrentLabel]);

    return (
      <ProgressStepperStepContext.Provider value={{ tone, interactive, active: isActive }}>
        <li
          aria-current={isActive ? 'step' : undefined}
          className={cx('c11n', styles, className)}
          data-c11n-component="ProgressStepper.Step"
          data-c11n-version={__C11N_VERSION__}
          ref={forwardedRef}
          {...rest}
        >
          <Component
            data-c11n-component="ProgressStepper.StepInner"
            href={interactive ? href : undefined}
            onClick={interactive ? handleClick : onClick}
            className="c11n"
          >
            {content}
          </Component>
        </li>
      </ProgressStepperStepContext.Provider>
    );
  },
);

ProgressStepperStep.displayName = 'ProgressStepper.Step';
